import React from 'react';

/**
 * KpiCards - bandeau d'indicateurs.
 *
 * <h3>Une couleur franche, pas un nuancier</h3>
 * L'itération précédente avait tout passé en blanc, avec un point de 7 px pour
 * toute couleur : c'était devenu illisible et sans relief. Ce qui rendait la
 * version d'avant fatigante n'était pas la couleur, c'était son INTENSITÉ - un
 * aplat pastel sur toute la carte, six fois de suite.
 *
 * La règle retenue : **la teinte porte la carte, la valeur porte la couleur, et
 * rien d'autre n'est colorié.** Fond pastel très clair, bordure assortie, valeur
 * dans la couleur d'accent. Un coup d'œil suffit à distinguer un compteur d'alerte
 * d'un compteur neutre, sans que l'écran devienne un damier.
 *
 * Les pictogrammes restent supprimés : une flamme ou une horloge sur chaque carte
 * n'ajoute rien que le libellé ne dise déjà. `icon` est donc toujours accepté et
 * **volontairement ignoré**, pour qu'aucun des vingt écrans appelants n'ait à être
 * retouché.
 *
 * Props par item : { label, value, color, bg?, suffix?, onClick?, active?, icon? }
 * `columns` : nombre de colonnes (défaut : items.length, plafonné à 4).
 */

const LABEL = '#667085';
const SUFFIX = '#98a2b3';
const ACCENT_DEFAUT = '#1a6b3c';

/* Teintes derivees de la couleur d'accent : `12` en hexa = ~7 % d'opacite pour le
   fond, `2e` = ~18 % pour la bordure. Assez pour teinter, trop peu pour saturer. */
const fondDe = (c, bg) => bg || `${c}12`;
const bordDe = (c) => `${c}2e`;

export default function KpiCards({ items = [], columns, style = {} }) {
    if (!items.length) return null;
    const cols = columns ? Math.min(columns, 6) : Math.min(items.length, 4);

    return (
        <div
            style={{
                display: 'grid',
                // minmax(0, 1fr) et non 1fr : sans le minimum a zero, une valeur longue
                // elargit sa colonne et desaligne toute la rangee.
                gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                gap: 12,
                marginBottom: 20,
                ...style,
            }}
        >
            {items.map((kpi, i) => {
                const color = kpi.color || ACCENT_DEFAUT;
                const cliquable = !!kpi.onClick;
                return (
                    <div
                        key={i}
                        onClick={kpi.onClick}
                        role={cliquable ? 'button' : undefined}
                        tabIndex={cliquable ? 0 : undefined}
                        onKeyDown={cliquable ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); kpi.onClick(); }
                        } : undefined}
                        style={{
                            background: fondDe(color, kpi.bg),
                            borderRadius: 14,
                            padding: '14px 16px',
                            border: `1px solid ${kpi.active ? color : bordDe(color)}`,
                            boxShadow: kpi.active ? `0 0 0 3px ${color}1f` : 'none',
                            cursor: cliquable ? 'pointer' : 'default',
                            transition: 'border-color 140ms ease, box-shadow 140ms ease',
                            minWidth: 0,
                        }}
                    >
                        <div style={{
                            fontSize: 12.5, color: LABEL, fontWeight: 500,
                            marginBottom: 4, whiteSpace: 'nowrap',
                            overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                            {kpi.label}
                        </div>

                        <div style={{
                            fontSize: 26, fontWeight: 700, color,
                            lineHeight: 1.1, letterSpacing: '-0.02em',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                            {kpi.value}
                        </div>

                        {kpi.suffix && (
                            <div style={{
                                fontSize: 11, color: SUFFIX, marginTop: 2,
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>
                                {kpi.suffix}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
